import {
    ConnectAccountOnboarding,
    ConnectNotificationBanner
} from "@stripe/react-connect-js";
import { useDispatch, useSelector } from "react-redux";
import { closeCurrentModal } from "store/modal/slice";
import { useCallback, useMemo, useRef } from "react";
import { Spinner } from "components/spinners/Spinner";
import styles from "./index.module.scss";
import useClickOutside from "hooks/useClickOutside";
import useKeyPress from "hooks/useKeyPress";
import { useUserStripeData } from "hooks/useUserStripeData";
import { useTranslation } from "react-i18next";
import Close from "assets/icons/close.svg?react";
import {
    getStripeAccount,
    pollStripeAccount
} from "store/stripeConnectAccount/thunks";
import { AppDispatch } from "store/store";
import { useCombineLoading } from "hooks/useCombineLoading";
import { StripeConnectComponentWrapper } from "components/stripe/StripeConnectComponentWrapper";
import { useRunOnce } from "hooks/useRunOnce";
import { NotificationCount } from "@stripe/connect-js";
import { Button } from "components/buttons/Button";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import { RootState } from "store/reducers";

export const StripeEmbeddedModal = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    const { isPollingStripeAccounts } = useSelector(
        (state: RootState) => state.stripeConnectAccount
    );

    const { accountData } = useUserStripeData({
        includeProducts: false,
        requestImmediately: true
    });

    const modalRef = useRef<HTMLDivElement>(null);

    const close = useCallback(async () => {
        if (
            accountData?.gatedContentStatus !==
                GatedContentStatus.STRIPE_ACCOUNT_NOT_SETUP ||
            confirm(
                t("gated-content-modal:on-close-warning-header") +
                    "\n" +
                    t("gated-content-modal:on-close-warning-text")
            )
        ) {
            await dispatch(getStripeAccount());
            dispatch(closeCurrentModal());
        }
    }, [accountData?.gatedContentStatus, dispatch, t]);

    useRunOnce(
        close,
        accountData?.gatedContentStatus === GatedContentStatus.READY
    );

    useClickOutside(modalRef, close);
    useKeyPress("Escape", close);

    const handleExit = useCallback(() => {
        dispatch(pollStripeAccount());
    }, [dispatch]);

    const handleNotificationChange = useCallback(
        (notificationData: NotificationCount) => {
            if (notificationData?.actionRequired === 0) {
                dispatch(getStripeAccount());
            }
        },
        [dispatch]
    );

    const loading = useCombineLoading(isPollingStripeAccounts, !accountData);

    const component = useMemo(() => {
        if (loading) {
            return (
                <>
                    {isPollingStripeAccounts && (
                        <p>{t("gated-content-page:polling-stripe-accounts")}</p>
                    )}
                    <Spinner />
                </>
            );
        }

        let innerComponent: React.ReactNode = null;

        switch (accountData?.gatedContentStatus) {
            case GatedContentStatus.STRIPE_ACCOUNT_NOT_SETUP:
                innerComponent = (
                    <ConnectAccountOnboarding onExit={handleExit} />
                );
                break;
            case GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED:
                innerComponent = (
                    <ConnectNotificationBanner
                        collectionOptions={{
                            fields: "eventually_due",
                            futureRequirements: "include"
                        }}
                        onNotificationsChange={handleNotificationChange}
                    />
                );
                break;
            case GatedContentStatus.READY:
                // Add component to manage account here
                return (
                    <div className={styles["stripe-embedded-modal-complete"]}>
                        {t("gated-content-modal:account-setup-complete")}
                        <Button onClick={close}>
                            {t("gated-content-modal:ready-cta")}
                        </Button>
                    </div>
                );
            default:
                break;
        }

        return (
            <StripeConnectComponentWrapper
                loadingView={<Spinner />}
                onSuccess={() => {
                    // DEVNOTE: A more appropriate way to do this would be to set the account data in the store directly
                    dispatch(getStripeAccount());
                }}
            >
                {innerComponent}
            </StripeConnectComponentWrapper>
        );
    }, [
        loading,
        accountData?.gatedContentStatus,
        isPollingStripeAccounts,
        t,
        handleExit,
        handleNotificationChange,
        close,
        dispatch
    ]);

    return (
        <div className={`prevent-scroll ${styles["stripe-embedded-modal"]}`}>
            <div
                ref={modalRef}
                className={styles["stripe-embedded-modal-content"]}
            >
                <div className={styles["stripe-embedded-modal-header"]}>
                    <Close onClick={close} />
                </div>
                {component}
            </div>
        </div>
    );
};
